import React, { useEffect, useMemo, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useBusStore } from "../../../store/busStore";
import { useSeatmapStore } from "../../../store/seatmapStore";

const normalizeTrainTypeCode = (code) => {
	const normalized = String(code || "").trim();
	if (!normalized) return "";
	if (normalized.startsWith("BUS_")) return normalized.replace("_", "");
	return normalized;
};

const formatSeatmapLabel = (layout) => {
	const typeCode =
		normalizeTrainTypeCode(layout?.train_type_code) ||
		String(layout?.train_type_name || "").trim();
	const name = String(layout?.name || "").trim();
	const variant = layout?.variant ? ` (${layout.variant})` : "";
	if (typeCode && name) return `${typeCode} - ${name}${variant}`;
	if (name) return `${name}${variant}`;
	if (typeCode) return `${typeCode}${variant}`;
	return "좌석배치도";
};

const formatDate = (value) => {
	if (!value) return "-";
	if (value.includes("-")) return value;
	if (value.length === 8) {
		return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6)}`;
	}
	return value;
};

export default function SelectServiceModal({
	isOpen,
	onClose,
	eventData,
	onConfirm,
}) {
	const { listServices } = useBusStore();
	const { layouts, fetchLayouts } = useSeatmapStore();

	const [services, setServices] = useState([]);
	const [selectedService, setSelectedService] = useState(null);
	const [selectedSeatmapId, setSelectedSeatmapId] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const busLayouts = useMemo(() => {
		return (layouts || []).filter((layout) => {
			const category = String(layout.train_type_category || "").toLowerCase();
			if (category) {
				return category === "bus";
			}
			return String(layout.train_type_code || "").toUpperCase().includes("BUS");
		});
	}, [layouts]);

	useEffect(() => {
		if (!isOpen || !eventData) return;
		const loadData = async () => {
			setLoading(true);
			setError(null);
			try {
				await fetchLayouts({ category: "bus" });
				const response = await listServices({
					ev_ym: eventData.ev_ym,
					ev_seq: eventData.ev_seq,
				});
				if (response.success) {
					setServices(response.data || []);
					if (response.data && response.data.length > 0) {
						setSelectedService(response.data[0]);
					}
				} else {
					setError(response.error || "버스 좌석 등록 목록을 불러오지 못했습니다.");
				}
			} catch (err) {
				console.error("버스 좌석 등록 로드 실패:", err);
				setError("버스 좌석 등록 목록을 불러오지 못했습니다.");
			} finally {
				setLoading(false);
			}
		};

		loadData();
	}, [isOpen, eventData, fetchLayouts, listServices]);

	useEffect(() => {
		if (!isOpen) {
			setServices([]);
			setSelectedService(null);
			setSelectedSeatmapId(null);
			setError(null);
		}
	}, [isOpen]);

	useEffect(() => {
		if (!selectedService) return;
		if (selectedService.seatmap_layout_id) {
			const match = busLayouts.find(
				(layout) => layout.id === selectedService.seatmap_layout_id
			);
			if (match) {
				setSelectedSeatmapId(match.id);
				return;
			}
		}
		if (!selectedSeatmapId && busLayouts.length > 0) {
			setSelectedSeatmapId(busLayouts[0].id);
		}
	}, [selectedService, busLayouts, selectedSeatmapId]);

	const handleConfirm = () => {
		if (!selectedService) {
			alert("버스 좌석 등록을 선택해주세요.");
			return;
		}

		const seatmapLayout = busLayouts.find(
			(layout) => layout.id === selectedSeatmapId
		);

		if (typeof onConfirm === "function") {
			onConfirm(selectedService, seatmapLayout || null);
		} else {
			onClose();
		}
	};

	if (!isOpen) return null;

	return (
		<div
			className="fixed inset-0 z-50 overflow-y-auto"
			aria-labelledby="modal-title"
			role="dialog"
			aria-modal="true"
		>
			<div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
				<div
					className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
					aria-hidden="true"
					onClick={onClose}
				></div>

				<span
					className="hidden sm:inline-block sm:align-middle sm:h-screen"
					aria-hidden="true"
				>
					&#8203;
				</span>

				<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
					<div className="bg-gray-50 px-4 py-3 border-b border-gray-200 sm:px-6 flex justify-between items-center">
						<div>
							<h3 className="text-lg leading-6 font-medium text-gray-900">
								버스 좌석 등록 선택
							</h3>
							<p className="text-sm text-gray-500">
								{eventData?.ev_nm || ""} ({eventData?.ev_ym}-{eventData?.ev_seq})
							</p>
						</div>
						<button
							type="button"
							className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
							onClick={onClose}
						>
							<span className="sr-only">닫기</span>
							<XMarkIcon className="h-6 w-6" aria-hidden="true" />
						</button>
					</div>

					<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
						{loading ? (
							<div className="flex justify-center items-center py-10">
								<div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
							</div>
						) : error ? (
							<div className="text-center py-8">
								<p className="text-red-500">{error}</p>
							</div>
						) : (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div className="border rounded-lg p-4">
									<h4 className="text-lg font-medium text-gray-900 mb-4">
										버스 좌석 등록
									</h4>
									<div className="space-y-2 max-h-[360px] overflow-y-auto">
										{services.length > 0 ? (
											services.map((service) => (
												<div
													key={service.bus_code}
													onClick={() => setSelectedService(service)}
													className={`p-3 border rounded-md cursor-pointer transition-colors ${
														selectedService?.bus_code === service.bus_code
															? "bg-blue-50 border-blue-500"
															: "hover:bg-gray-50"
													}`}
												>
													<div className="flex items-center justify-between">
														<div className="font-medium text-gray-900">
															{service.bus_code}
														</div>
														<span className="text-xs text-gray-500">
															{formatDate(service.service_date)}
														</span>
													</div>
													<div className="mt-2 flex items-center justify-between text-sm text-gray-600">
														<span>좌석 {service.seat_count || 40}석</span>
														<span>
															배정 {service.assigned_seats || 0} / 잔여 {service.available_seats || 0}
														</span>
													</div>
													{service.memo && (
														<div className="mt-1 text-xs text-gray-500">
															{service.memo}
														</div>
													)}
												</div>
											))
										) : (
											<div className="text-sm text-gray-500 text-center py-6">
												등록된 버스 좌석 등록이 없습니다.
											</div>
										)}
									</div>
								</div>

								<div className="border rounded-lg p-4">
									<h4 className="text-lg font-medium text-gray-900 mb-4">
										좌석배치도 선택
									</h4>
									{busLayouts.length > 0 ? (
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-2">
												선택된 배치도
											</label>
											<select
												value={selectedSeatmapId || ""}
												onChange={(e) => setSelectedSeatmapId(Number(e.target.value))}
												className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
											>
												{busLayouts.map((layout) => (
													<option key={layout.id} value={layout.id}>
														{formatSeatmapLabel(layout)}
													</option>
												))}
											</select>
											<p className="mt-2 text-xs text-gray-500">
												배치도는 좌석배치도 관리 메뉴에서 추가/수정할 수 있습니다.
											</p>
										</div>
									) : (
										<div className="text-sm text-gray-500">
											등록된 버스 좌석배치도가 없습니다. 기본 4열 배치로 진행됩니다.
										</div>
									)}
								</div>
							</div>
						)}
					</div>

					<div className="bg-gray-50 px-4 py-3 border-t border-gray-200 sm:px-6 flex justify-end gap-2">
						<button
							onClick={onClose}
							className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
						>
							취소
						</button>
						<button
							onClick={handleConfirm}
							className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
						>
							선택 완료
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
